import React, { useState } from 'react';
import { Compass, Plane, Trophy, Award, ArrowRight, MapPin, Shield, Home, FileText } from 'lucide-react';
import TravelEnquiryModal from './TravelEnquiryModal';
import WorldMapBackdrop from './WorldMapBackdrop';

// Shared enquiry-modal opener context via a simple event dispatch
export const openSportsFamilyEnquiry = () => {
  window.dispatchEvent(new CustomEvent('open-travel-enquiry', {
    detail: { focus: 'Sports family / elite young athlete — training and academic balance' }
  }));
};

const countries = [
  { name: 'Thailand', context: 'IGCSE group tuition' },
  { name: 'UAE (Dubai)', context: 'International family placement' },
  { name: 'Poland', context: 'European Masters Athletics — Toruń' },
  { name: 'Greece', context: 'European Masters League' },
  { name: 'Albania', context: 'European Masters League' },
  { name: 'Portugal (Madeira)', context: 'European Outdoor Championships' },
  { name: 'South Korea', context: 'World Masters Championships — Daegu' },
  { name: 'Sweden', context: 'World Masters Championships — Gothenburg' },
  { name: 'Italy', context: 'European Championships — Pescara' },
  { name: 'France', context: 'International competition' }
];

const credentials = [
  { icon: Award, label: 'Oxford PGCE (Distinction)', detail: 'University of Oxford, 2015' },
  { icon: Shield, label: 'PhD Immunology', detail: 'University of Wales (Distinction)' },
  { icon: Trophy, label: 'Team GB Masters Athlete', detail: '100m & 200m sprints — international' },
  { icon: Compass, label: 'Cambridge IGCSE Examiner', detail: 'Active examiner, secondary sciences & maths' }
];

const values = [
  {
    icon: Home,
    title: 'In-Residence Placement',
    body: 'Comfortable living within the family home for extended engagements — respectful, discreet, and integrated into the household rhythm.'
  },
  {
    icon: Plane,
    title: 'Global Travel Ready',
    body: 'Extensive international experience, current UK passport, and the composure to teach across time zones, hotels, tournaments, and relocations.'
  },
  {
    icon: Shield,
    title: 'Cultural Fluency',
    body: 'Deep regard for the values of hospitality, modesty, and family that anchor Gulf and international private-service work.'
  }
];

const TravelTutorSection = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [prefillFocus, setPrefillFocus] = useState('');

  React.useEffect(() => {
    const handler = (e) => {
      setPrefillFocus(e.detail?.focus || '');
      setModalOpen(true);
    };
    window.addEventListener('open-travel-enquiry', handler);
    return () => window.removeEventListener('open-travel-enquiry', handler);
  }, []);

  return (
    <>
    <TravelEnquiryModal open={modalOpen} onClose={() => { setModalOpen(false); setPrefillFocus(''); }} prefillFocus={prefillFocus} />
    <section
      id="travel-tutor"
      data-testid="travel-tutor-section"
      style={{
        background: '#0F172A',
        color: '#FFFCF5',
        padding: 'clamp(4rem, 8vw, 7rem) 2rem',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Subtle compass motif */}
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '-100px',
        width: '400px',
        height: '400px',
        opacity: 0.04,
        pointerEvents: 'none'
      }}>
        <Compass size={400} color="#C5A059" strokeWidth={0.5} />
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
        {/* Editorial section header */}
        <div style={{ marginBottom: '4rem', maxWidth: '760px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            fontSize: '0.72rem', fontWeight: 600,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            color: '#C5A059', marginBottom: '1.25rem',
            fontFamily: "'Inter', sans-serif"
          }}>
            <span style={{ width: '32px', height: '1px', background: '#C5A059' }} />
            For International Families
          </div>

          <h2 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
            fontWeight: 700,
            lineHeight: 1.1,
            marginBottom: '1.5rem',
            letterSpacing: '-0.02em'
          }}>
            An educator worthy of your family's<br />
            <span style={{ color: '#C5A059', fontStyle: 'italic' }}>most important role.</span>
          </h2>

          <p style={{
            fontSize: '1.15rem', lineHeight: 1.75,
            color: 'rgba(255, 252, 245, 0.75)',
            maxWidth: '640px',
            fontFamily: "'Inter', sans-serif"
          }}>
            Discerning families require more than a subject tutor. They require an educator who can hold
            the intellectual, emotional, and pastoral development of their children across residences,
            tournaments, and international relocations — with the grace and discretion of a family confidant.
          </p>
        </div>

        {/* Values row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '2rem',
          marginBottom: '5rem'
        }}>
          {values.map((v, i) => (
            <div
              key={i}
              data-testid={`travel-value-${i}`}
              style={{
                borderLeft: '2px solid #C5A059',
                paddingLeft: '1.5rem',
                paddingTop: '0.25rem'
              }}
            >
              <v.icon size={28} color="#C5A059" style={{ marginBottom: '1rem' }} />
              <h3 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: '1.4rem', fontWeight: 700,
                marginBottom: '0.75rem',
                color: '#FFFCF5',
                letterSpacing: '-0.01em'
              }}>
                {v.title}
              </h3>
              <p style={{
                color: 'rgba(255, 252, 245, 0.7)',
                fontSize: '0.95rem', lineHeight: 1.65,
                fontFamily: "'Inter', sans-serif"
              }}>
                {v.body}
              </p>
            </div>
          ))}
        </div>

        {/* Credentials strip */}
        <div style={{
          padding: '2.5rem',
          border: '1px solid rgba(197, 160, 89, 0.25)',
          background: 'rgba(197, 160, 89, 0.03)',
          marginBottom: '5rem'
        }}>
          <div style={{
            fontSize: '0.72rem', fontWeight: 600,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            color: '#C5A059', marginBottom: '2rem',
            fontFamily: "'Inter', sans-serif"
          }}>
            Credentials
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '2rem'
          }}>
            {credentials.map((c, i) => (
              <div
                key={i}
                data-testid={`credential-${i}`}
                style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}
              >
                <c.icon size={24} color="#C5A059" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <div style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontSize: '1.05rem', fontWeight: 600,
                    color: '#FFFCF5', marginBottom: '0.25rem'
                  }}>
                    {c.label}
                  </div>
                  <div style={{
                    color: 'rgba(255, 252, 245, 0.6)',
                    fontSize: '0.85rem',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    {c.detail}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* International footprint with world map backdrop */}
        <div style={{ marginBottom: '4rem', position: 'relative' }}>
          <WorldMapBackdrop />
          <div style={{ position: 'relative' }}>
          <div style={{
            fontSize: '0.72rem', fontWeight: 600,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            color: '#C5A059', marginBottom: '1.5rem',
            fontFamily: "'Inter', sans-serif"
          }}>
            International Footprint
          </div>

          <h3 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
            fontWeight: 700,
            marginBottom: '2rem',
            color: '#FFFCF5',
            maxWidth: '640px',
            lineHeight: 1.2,
            letterSpacing: '-0.02em'
          }}>
            Countries lived in, taught in, and competed in as a Team GB athlete.
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            {countries.map((c, i) => (
              <div
                key={i}
                data-testid={`country-${i}`}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                  padding: '1rem 1.25rem',
                  background: 'rgba(255, 252, 245, 0.03)',
                  borderBottom: '1px solid rgba(197, 160, 89, 0.15)'
                }}
              >
                <MapPin size={16} color="#C5A059" style={{ flexShrink: 0, marginTop: '3px' }} />
                <div>
                  <div style={{
                    color: '#FFFCF5', fontWeight: 600,
                    fontSize: '0.95rem',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    {c.name}
                  </div>
                  <div style={{
                    color: 'rgba(255, 252, 245, 0.55)',
                    fontSize: '0.8rem', marginTop: '2px',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    {c.context}
                  </div>
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>

        {/* CTA block */}
        <div style={{
          padding: 'clamp(2.5rem, 5vw, 4rem)',
          background: 'rgba(197, 160, 89, 0.06)',
          border: '1px solid rgba(197, 160, 89, 0.25)',
          textAlign: 'center',
          maxWidth: '860px',
          margin: '0 auto'
        }}>
          <h3 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
            fontWeight: 700,
            color: '#FFFCF5',
            marginBottom: '1rem',
            letterSpacing: '-0.02em',
            lineHeight: 1.2
          }}>
            Currently accepting <span style={{ color: '#C5A059', fontStyle: 'italic' }}>select</span> international placements
          </h3>

          <p style={{
            color: 'rgba(255, 252, 245, 0.7)',
            fontSize: '1rem', lineHeight: 1.7,
            marginBottom: '2rem',
            maxWidth: '580px',
            margin: '0 auto 2rem',
            fontFamily: "'Inter', sans-serif"
          }}>
            Enquiries are handled personally and in confidence. Please share the shape of your requirement
            and Dr. Omakobia will respond within 48 hours.
          </p>

          <div style={{
            display: 'flex', gap: '1rem',
            justifyContent: 'center', flexWrap: 'wrap'
          }}>
            <button
              onClick={() => setModalOpen(true)}
              data-testid="travel-tutor-enquiry-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: '#C5A059', color: '#0F172A',
                padding: '1rem 2rem', borderRadius: '4px',
                cursor: 'pointer', fontWeight: 700,
                fontFamily: "'Inter', sans-serif",
                border: '1px solid #C5A059',
                letterSpacing: '0.02em',
                transition: 'all 0.3s ease',
                fontSize: '0.95rem'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#FFFCF5'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#C5A059'; }}
            >
              <FileText size={18} />
              Complete Confidential Enquiry
            </button>
            <a
              href="https://calendly.com/drmomakobia/free-consultation"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="travel-tutor-calendly-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: 'transparent', color: '#FFFCF5',
                padding: '1rem 2rem', borderRadius: '4px',
                textDecoration: 'none', fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                border: '1px solid rgba(255, 252, 245, 0.35)',
                letterSpacing: '0.02em',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#C5A059'; e.currentTarget.style.color = '#C5A059'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255, 252, 245, 0.35)'; e.currentTarget.style.color = '#FFFCF5'; }}
            >
              Book a Zoom Call
              <ArrowRight size={18} />
            </a>
            <a
              href="mailto:drmomakobia@abbaacademy.co.uk?subject=In-Residence%20Travel%20Tutor%20Enquiry"
              data-testid="travel-tutor-email-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: 'transparent', color: '#FFFCF5',
                padding: '1rem 2rem', borderRadius: '4px',
                textDecoration: 'none', fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                border: '1px solid rgba(255, 252, 245, 0.35)',
                letterSpacing: '0.02em',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#C5A059'; e.currentTarget.style.color = '#C5A059'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255, 252, 245, 0.35)'; e.currentTarget.style.color = '#FFFCF5'; }}
            >
              Email Directly
            </a>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default TravelTutorSection;
